import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import DialogShell from '../../tasks/components/DialogShell';
import { attendanceApi, hhmm, RegisterCell, StatusOverride, statusStyle } from '../payroll.api';

export interface DayTarget {
  employeeId: number;
  employeeName: string | null;
  cell: RegisterCell;
  remarks?: string | null;
}

/**
 * HR's correction to one employee's day: the punch times, and/or a status that overrides what
 * the punches would give. The month's payroll goes stale and recalculates before its next report.
 */
export default function DayEditDialog({
  target,
  onClose,
  onSaved,
}: {
  target: DayTarget | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [punchIn, setPunchIn] = useState('');
  const [punchOut, setPunchOut] = useState('');
  const [override, setOverride] = useState<StatusOverride | ''>('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setPunchIn(hhmm(target.cell.punchIn));
    setPunchOut(hhmm(target.cell.punchOut));
    setOverride((target.cell.override as StatusOverride) || '');
    setRemarks(target.remarks ?? target.cell.remarks ?? '');
  }, [target]);

  const save = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await attendanceApi.saveDay({
        employeeId: target.employeeId,
        date: target.cell.date,
        punchIn: punchIn || null,
        punchOut: punchOut || null,
        statusOverride: override || null,
        remarks: remarks.trim() || null,
      });
      toast.success('Attendance saved.');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(logError({ message: 'Could not save attendance', error: e, code: 'ATTENDANCE_DAY_SAVE_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const current = target ? statusStyle(target.cell.status) : null;

  return (
    <DialogShell open={target !== null} title='CORRECT ATTENDANCE' onClose={onClose} onSubmit={save} busy={busy}>
      {target && (
        <Typography variant='body2'>
          <b>{target.employeeName}</b> —{' '}
          {new Date(`${target.cell.date}T00:00:00`).toLocaleDateString('en-IN', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          {current ? ` · now ${current.label}` : ''}
        </Typography>
      )}
      <Stack direction='row' gap={2}>
        <TextField
          label='In time'
          type='time'
          size='small'
          fullWidth
          value={punchIn}
          onChange={(e) => setPunchIn(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label='Out time'
          type='time'
          size='small'
          fullWidth
          value={punchOut}
          onChange={(e) => setPunchOut(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <TextField
        select
        size='small'
        label='Status override'
        value={override}
        onChange={(e) => setOverride(e.target.value as StatusOverride | '')}
        helperText='Leave blank to let the punch times and office rules decide.'
      >
        <MenuItem value=''>— From punch times —</MenuItem>
        <MenuItem value='PRESENT'>Present</MenuItem>
        <MenuItem value='HALF_DAY'>Half day</MenuItem>
        <MenuItem value='ABSENT'>Absent</MenuItem>
      </TextField>
      <TextField
        label='Remarks'
        size='small'
        multiline
        minRows={2}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        placeholder='e.g. Site visit, forgot to punch'
      />
    </DialogShell>
  );
}
